import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { css } from '@emotion/react';
import { ProColors } from '../aura';
import { getSitekey } from '../features/turnstile-constants';
import { Wave5 } from '../aphrodite';
import { useFormContext } from '../state';
import { useFormErrors } from './input/helpers/use-form-errors';
import { useValidator } from './input/helpers/use-validation';
import { Form, Title } from './templates';

const TURNSTILE_SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js';

/**
 * Renders the Cloudflare Turnstile widget and stores the resulting token in
 * form context so the step machine can pass it to authenticateToConnect().
 *
 * Risk mitigations:
 *  1. Stale token cleared on mount — any token persisted to localStorage in a
 *     previous session is wiped before the widget initialises, ensuring the
 *     step machine always consumes a token issued during the current page load.
 *  2. Validator blocks "Next" until a live token exists — the submit button
 *     stays disabled while the widget is loading or after a token expiry.
 *  3. expired-callback / error-callback clear the stored token — if Cloudflare
 *     invalidates the token, the stored value is removed so a retry cannot
 *     submit a stale string.
 *  4. Auth failure is surfaced via useFormErrors — when the step machine sets
 *     a 'turnstileToken' field error, the message appears below the widget
 *     and is cleared automatically when a new token arrives.
 *  5. Script event listener is always removed on unmount — the cleanup
 *     captures the exact script element that received addEventListener and
 *     calls removeEventListener on it, preventing listener accumulation across
 *     back-and-forward navigations.
 *  6. onToken identity is stable — clearAuthError is accessed through a ref
 *     rather than via the useCallback dep array, so onToken never changes
 *     identity due to auth-state changes. This prevents the widget render
 *     effect from re-running on every auth failure, which would destroy and
 *     recreate the Cloudflare widget mid-interaction.
 */
export function StepTurnstile() {
  const { t } = useTranslation('stepTurnstile');
  const { data, setData } = useFormContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const siteKey = getSitekey(window.location.hostname) ?? '';

  // ── Fix 4: read auth-failure error set by the step machine ───────────────
  const { props: errorProps, clear: clearAuthError } =
    useFormErrors('turnstileToken');

  // ── Fix 1: wipe stale token from localStorage on mount ───────────────────
  // setData is a stable reference (memoised in FormContextProvider), so it is
  // intentionally omitted from the dependency array to run exactly once on
  // mount and avoid a second clear when setData is re-created.
  useEffect(() => {
    setData(prev => ({ ...prev, turnstileToken: undefined }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- see comment above

  const clearToken = useCallback(() => {
    setData(prev => ({ ...prev, turnstileToken: undefined }));
  }, [setData]);

  // ── Fix 6: stable onToken via ref ────────────────────────────────────────
  // clearAuthError changes identity whenever formContext.state changes (it is
  // produced by a useCallback whose dep is `state`). If clearAuthError were
  // listed as a dep of onToken, onToken would also change identity, causing
  // the widget render effect to re-run on every auth failure — destroying and
  // recreating the Cloudflare widget just as the user needs to retry.
  // Holding clearAuthError in a ref and always keeping it current gives
  // onToken a stable identity while still calling the latest version.
  const clearAuthErrorRef = useRef(clearAuthError);
  clearAuthErrorRef.current = clearAuthError;

  const onToken = useCallback(
    (token: string) => {
      clearAuthErrorRef.current();
      setData(prev => ({ ...prev, turnstileToken: token }));
    },
    [setData], // stable — clearAuthError intentionally accessed via ref above
  );

  // ── Fix 3: clear token when Cloudflare invalidates it ────────────────────
  const onExpire = useCallback(() => {
    clearToken();
  }, [clearToken]);

  const onError = useCallback(() => {
    clearToken();
  }, [clearToken]);

  // ── Fix 5: load Turnstile script with leak-free cleanup ──────────────────
  useEffect(() => {
    const renderWidget = () => {
      if (!containerRef.current || !window.turnstile) return;
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        callback: onToken,
        'expired-callback': onExpire,
        'error-callback': onError,
      });
    };

    // Track which script element received the listener so cleanup can remove
    // it precisely, regardless of which branch (existing / new) was taken.
    let scriptEl: HTMLScriptElement | null = null;

    if (window.turnstile) {
      // Script already loaded — render immediately, no listener needed.
      renderWidget();
    } else {
      const existing = document.querySelector<HTMLScriptElement>(
        `script[src="${TURNSTILE_SCRIPT_SRC}"]`,
      );
      scriptEl = existing ?? document.createElement('script');
      if (!existing) {
        scriptEl.src = TURNSTILE_SCRIPT_SRC;
        scriptEl.async = true;
        // Surface real error details to Datadog instead of an opaque
        // cross-origin "Script error." (Cloudflare sends Access-Control-Allow-Origin).
        scriptEl.crossOrigin = 'anonymous';
        document.head.appendChild(scriptEl);
      }
      scriptEl.addEventListener('load', renderWidget);
    }

    return () => {
      // Remove the listener from whichever script element we attached to.
      // scriptEl is null only when window.turnstile was already present, in
      // which case no listener was added and this is a safe no-op.
      scriptEl?.removeEventListener('load', renderWidget);
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, onToken, onExpire, onError]);

  // ── Fix 2: block Next until a live token exists ───────────────────────────
  useValidator(
    useCallback(
      formData => (formData.turnstileToken ? null : t('validationError')),
      [t],
    ),
  );

  return (
    <Form>
      <div css={css({ width: 64, margin: 'auto' })}>
        <Wave5 color={ProColors.yellow[600]} strokeWidth={1.4} />
      </div>
      <Title text={t('title')} />
      <p className="aura--copy-normal" css={css({ textAlign: 'center' })}>
        {t('description')}
      </p>
      {/* The Turnstile widget is injected into this div by the CF script. */}
      <div
        ref={containerRef}
        css={css({ display: 'flex', justifyContent: 'center', marginTop: 16 })}
      />
      {data.turnstileToken === undefined && !errorProps.subText && (
        <p
          css={css({ color: ProColors.navy[400], textAlign: 'center' })}
          className="aura--copy-small"
        >
          {t('loading')}
        </p>
      )}
      {/* ── Fix 4: render auth-failure error from the step machine ── */}
      {errorProps.subText && (
        <p
          css={css({ color: ProColors.coral[600], textAlign: 'center' })}
          className="aura--copy-small"
        >
          {errorProps.subText}
        </p>
      )}
    </Form>
  );
}
